import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { useCallback } from "react";
import Annnotation from "~/components/build/Show/components/Annotation";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { jobAnnotationsQueryOptions } from "~/components/build/Show/lib/queries/jobAnnotations";
import { Job } from "~/components/build/Show/lib/types";
import { useReactRouterNavigateHandler } from "~/components/build/Show/lib/useReactRouterNavigateHandler";
import EmptyState from "~/components/shared/EmptyState";
import Spinner from "~/components/shared/Spinner";

type Props = {
  job: Job;
};

export default function JobAnnotations({ job }: Props) {
  const { build } = useBuild();
  const navigateClickHandler = useReactRouterNavigateHandler();

  const { data, isLoading, hasNextPage, fetchNextPage, isFetchingNextPage } = useInfiniteQuery(
    jobAnnotationsQueryOptions(build, job, {
      placeholderData: keepPreviousData,
    }),
  );

  const { annotationsCount } = job.statistics;

  const handleShowMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  if (!annotationsCount) {
    return (
      <div className="my-2.5 flex w-full flex-1 items-center justify-center bg-slate-100">
        <EmptyState
          heading="No annotations"
          subheading={
            <>
              <span>
                There are no annotations for this job. To scope annotations to your job, use the{" "}
              </span>
              <a
                href="https://buildkite.com/docs/agent/v3/cli-annotate"
                target="_blank"
                rel="noopener noreferrer"
                className="monospace"
              >
                buildkite-agent annotate --scope=job
              </a>
              <span> command.</span>
            </>
          }
        />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="mb-3 flex-1 pt-3">
        <div className="flex w-full items-center space-x-2">
          <Spinner className="h-4 w-4" />
          <span>Fetching annotations...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-3 flex w-full flex-1 flex-col pt-3" onClick={navigateClickHandler}>
      {data?.pages.flatMap((page) => {
        return page.records.map((annotation) => (
          <Annnotation key={annotation.id} annotation={annotation} />
        ));
      })}

      {hasNextPage && (
        <div className="flex w-full items-center justify-center">
          {isFetchingNextPage ? (
            <div className="space-x-2">
              <Spinner className="h-4 w-4" />
              <span>Fetching annotations...</span>
            </div>
          ) : (
            <button className="btn btn-small btn-depth" onClick={handleShowMore}>
              Show more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
